import { useMemo, useState, type ReactNode } from 'react';
import {
  RESPONSE_TO_LEGACY_STATUS,
  RSVP_RESPONSES,
  createEventGuestSchema,
  partyAdults,
  splitAdults,
  updateGuestSchema,
  type Guest,
  type GuestCapabilities,
  type GuestContactInput,
  type GuestFormOptions,
  type GuestPartyMemberInput,
  type RsvpResponseValue,
} from '@peles/shared';
import { EntityModal } from '../../common/EntityModal';
import { SearchableListSelect } from '../../common/SearchableListSelect';
import { GoogleAddressAutocomplete } from '../../common/GoogleAddressAutocomplete';
import { GuestContactsSection } from '../GuestContactsSection';
import { useCreateEventGuest, useUpdateEventGuest } from '../../../hooks/useEventGuests';
import { useDialog } from '../../../context/DialogContext';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { SeatPicker } from '../list/SeatPicker';
import { useSeatGuests } from '../list/useSeatGuests';
import { PartyMembersEditor } from './PartyMembersEditor';
import { withMemberAttending } from './memberAttending';
import { GuestRelationsEditor } from './GuestRelationsEditor';
import { Stepper } from './Stepper';
import { PhoneInput } from '../../common/PhoneInput';

export interface GuestFormModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** The guest being edited; `null` adds one. */
  guest: Guest | null;
  capabilities: GuestCapabilities;
  options: GuestFormOptions | undefined;
}

interface FormState {
  firstName: string;
  lastName: string;
  side: string;
  familyGroup: string;
  tags: string[];
  email: string;
  phone: string;
  address: string;
  contacts: GuestContactInput[];
  maleCount: number;
  femaleCount: number;
  kidCount: number;
  partyMembers: GuestPartyMemberInput[];
  response: RsvpResponseValue;
  dietaryTags: string[];
  dietaryNotes: string;
  mealPreference: string;
  keepTogether: boolean | null;
  notes: string;
  internalNotes: string;
  tableNumber: number | null;
}

function initialState(guest: Guest | null): FormState {
  return {
    firstName: guest?.firstName ?? '',
    lastName: guest?.lastName ?? '',
    side: guest?.side ?? '',
    familyGroup: guest?.familyGroup ?? '',
    tags: guest?.tags ?? [],
    email: guest?.email ?? '',
    phone: guest?.phone ?? '',
    address: guest?.address ?? '',
    contacts: (guest?.contacts ?? []).map((c) => ({
      contactType: c.contactType,
      value: c.value,
      label: c.label ?? '',
    })),
    maleCount: guest?.maleCount ?? 1,
    femaleCount: guest?.femaleCount ?? 0,
    kidCount: guest?.kidCount ?? 0,
    partyMembers: (guest?.partyMembers ?? []).map((m) => ({
      id: m.id,
      name: m.name,
      memberType: m.memberType,
      gender: m.gender ?? null,
      email: m.email,
      phone: m.phone,
      isPrimary: m.isPrimary,
      mealOptionId: m.mealOptionId,
      dietaryNotes: m.dietaryNotes,
      attending: m.attending,
    })),
    response: guest?.response ?? 'PENDING',
    dietaryTags: guest?.dietaryTags ?? [],
    dietaryNotes: guest?.dietaryNotes ?? '',
    mealPreference: guest?.mealPreference ?? '',
    keepTogether: guest?.keepTogether ?? null,
    notes: guest?.notes ?? '',
    internalNotes: guest?.internalNotes ?? '',
    tableNumber: guest?.tableNumber ?? null,
  };
}

const inputClass =
  'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function Field({
  label,
  htmlFor,
  error,
  children,
  className = '',
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

const optional = (value: string) => value.trim() || undefined;
const nullable = (value: string) => value.trim() || null;

/**
 * Add or edit a guest — one form for the producer and the client portal,
 * shaped by what the caller may do: who they are, the party (with named
 * people and their meals, even before the first save), how to reach them,
 * their answer and dietary needs, where they sit and with whom, and notes.
 * The office's own fields (the men/women split, internal notes, VIP) are
 * offered to the office only; the server holds the same line.
 *
 * Checked with the shared schema before it is sent, errors shown beside the
 * field. A table chosen here is taken through the seating service after the
 * save, so a full table is asked about rather than silently overfilled.
 */
export function GuestFormModal({
  open,
  onClose,
  eventId,
  guest: given,
  capabilities,
  options,
}: GuestFormModalProps) {
  const [form, setForm] = useState<FormState>(() => initialState(given));
  // A guest added here whose table then could not be had: saved, so the form
  // carries on as its edit and never adds it twice.
  const [added, setAdded] = useState<Guest | null>(null);
  const guest = given ?? added;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const create = useCreateEventGuest(eventId);
  const update = useUpdateEventGuest(eventId);
  const { seat } = useSeatGuests(eventId);
  const { alert } = useDialog();
  const producer = capabilities.producerFields;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const adults = partyAdults(form);
  const people = adults + form.kidCount;
  const tagOptions = useMemo(
    () => (options?.tags ?? []).filter((t) => producer || t.value !== 'vip'),
    [options, producer],
  );
  const mealOptions = options?.mealOptions ?? [];

  const payload = () => {
    const common = {
      firstName: form.firstName.trim(),
      side: form.side || undefined,
      tags: form.tags,
      dietaryTags: form.dietaryTags,
      maleCount: form.maleCount,
      femaleCount: form.femaleCount,
      kidCount: form.kidCount,
      contacts: form.contacts
        .filter((c) => c.value.trim())
        .map((c) => ({ ...c, value: c.value.trim(), label: c.label || null })),
      // A blank row never saved is dropped; one that was saved keeps its place,
      // so clearing its name is a mistake to show, not a silent deletion.
      partyMembers: form.partyMembers
        .filter((m) => m.id || m.name.trim())
        .map((m) => ({
          ...m,
          name: m.name.trim(),
          email: m.email || null,
          phone: m.phone || null,
        })),
      keepTogether: form.keepTogether,
      ...(capabilities.rsvp ? { rsvpStatus: RESPONSE_TO_LEGACY_STATUS[form.response] } : {}),
    };
    if (!guest) {
      return {
        ...common,
        lastName: optional(form.lastName),
        familyGroup: optional(form.familyGroup),
        email: optional(form.email),
        phone: optional(form.phone),
        address: optional(form.address),
        dietaryNotes: optional(form.dietaryNotes),
        mealPreference: optional(form.mealPreference),
        notes: optional(form.notes),
        ...(producer ? { internalNotes: optional(form.internalNotes) } : {}),
      };
    }
    const edit = {
      ...common,
      side: form.side || null,
      lastName: nullable(form.lastName),
      familyGroup: nullable(form.familyGroup),
      email: nullable(form.email),
      phone: nullable(form.phone),
      address: nullable(form.address),
      dietaryNotes: nullable(form.dietaryNotes),
      mealPreference: nullable(form.mealPreference),
      notes: nullable(form.notes),
      ...(producer ? { internalNotes: nullable(form.internalNotes) } : {}),
    };
    // An unchanged answer is not re-recorded (it would restamp who answered).
    if (form.response === guest.response) delete (edit as { rsvpStatus?: string }).rsvpStatus;
    return edit;
  };

  const submit = async () => {
    const body = payload();
    const check = (guest ? updateGuestSchema : createEventGuestSchema).safeParse(body);
    const found: Record<string, string> = {};
    if (!body.firstName) found.firstName = 'A first name is needed';
    if (people < 1) found.maleCount = 'A party has at least one person';
    if (!check.success) {
      for (const issue of check.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        if (key === 'contacts' || key === 'partyMembers') found[key] = 'Check these entries';
        else found[key] ??= issue.message;
      }
    }
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }

    setSaving(true);
    try {
      const saved = guest
        ? await update.mutateAsync({ guestId: guest.id, updates: body })
        : await create.mutateAsync(body as never);
      if (capabilities.seating && form.tableNumber !== (guest?.tableNumber ?? null)) {
        const seated = await seat({ guestIds: [saved.id], tableNumber: form.tableNumber });
        if (!seated) {
          // The guest is saved; the table is not. Say so where the table is
          // chosen, and stay open for another choice.
          if (!guest) setAdded({ ...saved, tableNumber: null, seatNumber: null });
          setErrors({
            tableNumber: `Saved, but not seated at Table ${form.tableNumber} — choose another table, or Not seated.`,
          });
          return;
        }
      }
      onClose();
    } catch (err) {
      await alert({
        title: guest ? 'Could not save the guest' : 'Could not add the guest',
        message: extractApiErrorMessage(err, 'Nothing was saved.'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={guest ? 'Edit guest' : 'Add guest'}
      size="lg"
      closeOnBackdrop={false}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : guest ? 'Save' : 'Add guest'}
          </button>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-6"
        noValidate
      >
        <Section title="Who">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" htmlFor="guest-first-name" error={errors.firstName}>
              <input
                id="guest-first-name"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                autoFocus
                aria-invalid={!!errors.firstName}
                className={inputClass}
              />
            </Field>
            <Field label="Last name" htmlFor="guest-last-name" error={errors.lastName}>
              <input
                id="guest-last-name"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                className={inputClass}
              />
            </Field>
            {(options?.sides.length ?? 0) > 0 && (
              <Field label="Side">
                <SearchableListSelect
                  items={options?.sides ?? []}
                  value={form.side || null}
                  onChange={(v) => set('side', (v as string) || '')}
                  getValue={(o) => o.value}
                  getLabel={(o) => o.label}
                  placeholder="Choose a side"
                  ariaLabel="Side"
                  clearable
                />
              </Field>
            )}
            <Field label="Family group" htmlFor="guest-family">
              <input
                id="guest-family"
                list="guest-family-options"
                value={form.familyGroup}
                onChange={(e) => set('familyGroup', e.target.value)}
                className={inputClass}
              />
              <datalist id="guest-family-options">
                {(options?.familyGroups ?? []).map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </Field>
            {tagOptions.length > 0 && (
              <Field label="Tags" className="sm:col-span-2">
                <SearchableListSelect
                  items={tagOptions}
                  value={form.tags.filter((t) => producer || t !== 'vip')}
                  onChange={(v) =>
                    set(
                      'tags',
                      producer
                        ? (v as string[])
                        : [...(v as string[]), ...form.tags.filter((t) => t === 'vip')],
                    )
                  }
                  getValue={(o) => o.value}
                  getLabel={(o) => o.label}
                  placeholder="Add tags"
                  ariaLabel="Tags"
                  multiple
                />
              </Field>
            )}
          </div>
        </Section>

        <Section title="Party">
          <div className="flex flex-wrap gap-4">
            {producer ? (
              <>
                <Stepper label="Men" value={form.maleCount} onChange={(n) => set('maleCount', n)} />
                <Stepper
                  label="Women"
                  value={form.femaleCount}
                  onChange={(n) => set('femaleCount', n)}
                />
              </>
            ) : (
              <Stepper
                label="Adults"
                value={adults}
                onChange={(n) => {
                  const split = splitAdults(
                    { maleCount: form.maleCount, femaleCount: form.femaleCount },
                    n,
                  );
                  setForm((f) => ({ ...f, ...split }));
                }}
              />
            )}
            <Stepper label="Children" value={form.kidCount} onChange={(n) => set('kidCount', n)} />
          </div>
          <p className={`text-xs ${errors.maleCount ? 'text-red-700' : 'text-gray-500'}`}>
            {errors.maleCount ?? `${people} ${people === 1 ? 'person' : 'people'} in this party`}
          </p>
          <PartyMembersEditor
            members={form.partyMembers}
            onChange={(members) => set('partyMembers', members)}
            mealOptions={mealOptions}
            onAttendingChange={(member, attending) =>
              setForm((f) => withMemberAttending(f, member, attending))
            }
          />
          {errors.partyMembers && <p className="text-xs text-red-700">{errors.partyMembers}</p>}
        </Section>

        <Section title="Contact">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email" htmlFor="guest-email" error={errors.email}>
              <input
                id="guest-email"
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                aria-invalid={!!errors.email}
                className={inputClass}
              />
            </Field>
            <Field label="Phone" htmlFor="guest-phone" error={errors.phone}>
              <PhoneInput
                id="guest-phone"
                value={form.phone}
                onChange={(phone) => set('phone', phone)}
                className={inputClass}
              />
            </Field>
            <Field label="Address" htmlFor="guest-address" className="sm:col-span-2">
              <GoogleAddressAutocomplete
                id="guest-address"
                value={form.address}
                onChange={(v) => set('address', v)}
                onAddressSelect={(fields) =>
                  set('address', fields.formattedAddress || fields.address || form.address)
                }
                className={inputClass}
              />
            </Field>
          </div>
          <GuestContactsSection
            contacts={form.contacts}
            onChange={(contacts) => set('contacts', contacts)}
          />
          {errors.contacts && <p className="text-xs text-red-700">{errors.contacts}</p>}
        </Section>

        <Section title="RSVP and meals">
          {capabilities.rsvp && (
            <div role="radiogroup" aria-label="RSVP" className="flex flex-wrap gap-2">
              {RSVP_RESPONSES.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={form.response === o.value}
                  onClick={() => set('response', o.value)}
                  className={`px-3.5 py-2 rounded-full text-sm border ${
                    form.response === o.value
                      ? 'bg-gray-900 border-gray-900 text-gray-50'
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(options?.dietaryTags.length ?? 0) > 0 && (
              <Field label="Dietary needs">
                <SearchableListSelect
                  items={options?.dietaryTags ?? []}
                  value={form.dietaryTags}
                  onChange={(v) => set('dietaryTags', v as string[])}
                  getValue={(o) => o.value}
                  getLabel={(o) => o.label}
                  placeholder="Add dietary needs"
                  ariaLabel="Dietary needs"
                  multiple
                />
              </Field>
            )}
            <Field label="Dietary notes" htmlFor="guest-dietary-notes">
              <input
                id="guest-dietary-notes"
                value={form.dietaryNotes}
                onChange={(e) => set('dietaryNotes', e.target.value)}
                className={inputClass}
              />
            </Field>
            {mealOptions.length === 0 && (
              <Field label="Meal preference" htmlFor="guest-meal">
                <input
                  id="guest-meal"
                  value={form.mealPreference}
                  onChange={(e) => set('mealPreference', e.target.value)}
                  className={inputClass}
                />
              </Field>
            )}
          </div>
        </Section>

        <Section title="Seating">
          {capabilities.seating && (
            <Field label="Table" error={errors.tableNumber}>
              <SeatPicker
                eventId={eventId}
                tableNumber={form.tableNumber}
                partySize={Math.max(1, people)}
                variant="field"
                label={form.tableNumber != null ? `Table ${form.tableNumber}` : 'Not seated'}
                onChoose={(table) => set('tableNumber', table)}
              />
            </Field>
          )}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">
              Keep the party at one table
            </span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Keep together">
              {[
                { value: null, label: 'Seating default' },
                { value: true, label: 'Yes' },
                { value: false, label: 'May split' },
              ].map((o) => (
                <button
                  key={String(o.value)}
                  type="button"
                  role="radio"
                  aria-checked={form.keepTogether === o.value}
                  onClick={() => set('keepTogether', o.value)}
                  className={`px-3.5 py-2 rounded-full text-sm border ${
                    form.keepTogether === o.value
                      ? 'bg-gray-900 border-gray-900 text-gray-50'
                      : 'bg-white border-gray-300 text-gray-700'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          {capabilities.relations &&
            (guest ? (
              <GuestRelationsEditor eventId={eventId} guestId={guest.id} />
            ) : (
              <p className="text-xs text-gray-500">
                Who to seat together or apart can be chosen once the guest is added.
              </p>
            ))}
        </Section>

        <Section title="Notes">
          <Field label="Notes" htmlFor="guest-notes">
            <textarea
              id="guest-notes"
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              rows={3}
              className={`${inputClass} resize-y`}
            />
          </Field>
          {producer && (
            <Field label="Internal notes (the office only)" htmlFor="guest-internal-notes">
              <textarea
                id="guest-internal-notes"
                value={form.internalNotes}
                onChange={(e) => set('internalNotes', e.target.value)}
                rows={2}
                className={`${inputClass} resize-y bg-amber-50`}
              />
            </Field>
          )}
        </Section>
      </form>
    </EntityModal>
  );
}
